<template>
  <div class="flex flex-col space-y-4">
    <lf-h4>
      {{ $t("ORGANIZATION.COMMUNICATION_TEMPLATES.ENTER_UTM_PARAMETER") }}
    </lf-h4>
    <div class="flex flex-col space-y-4">
      <div
        v-for="({ type, value, id }, index) in selectedFields"
        :key="id"
        class="flex w-full items-center justify-between"
      >
        <div class="flex w-5/6 space-x-3">
          <div class="w-1/2">
            <lf-dropdown
              :options="typeOptions"
              :value="type"
              :placeholder="$t('DEALS.TRACKING.UTM_TYPE')"
              name="type"
              full-width
              @change="selectUtmType(Number($event), index)"
            />
          </div>
          <div class="w-1/2">
            <lf-input
              :placeholder="$t('DEALS.TRACKING.UTM_VALUE')"
              :value="value"
              :name="type"
              :maxlength="255"
              :data-cy="`utm-value-input-${index}`"
              class="!mb-0 h-10"
              @key-released="updateValue($event, id)"
            />
          </div>
        </div>
        <div class="min-w-20">
          <div class="flex items-center gap-x-2">
            <div class="min-w-8">
              <button
                v-if="index"
                v-tooltip="`${$t('COMMON.DELETE')} ${$t('COMMON.TYPE')}`"
                type="button"
                :data-cy="`delete-utm-type-${index}`"
                class="w-fit-content justify-self-end rounded-4 p-2"
                @click="deleteType(id)"
              >
                <icon-base :icon="IconDelete" />
              </button>
            </div>
            <button
              v-if="
                typeOptions.length &&
                !!value &&
                index === selectedFields.length - 1
              "
              v-tooltip="`${$t('COMMON.ADD')} ${$t('COMMON.TYPE')}`"
              :data-cy="`add-utm-type-${index}`"
              type="button"
              class="w-fit-content justify-self-end rounded-4 p-4"
              @click="addType"
            >
              <icon-base :icon="IconAdd" :width="20" :height="20" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";
import IconAdd from "@/components/icons/deals/IconAdd.vue";
import IconBase from "@/components/ui/IconBase.vue";
import IconDelete from "@/components/icons/IconDelete.vue";
import LfH4 from "@/components/ui/text/LfHeading4.vue";

import { ref, computed } from "vue";
import { UTM_TYPES } from "@/helpers/constants/tracking";
import { v4 } from "uuid";

const emit = defineEmits<{
  "value:change": [{ type: string; value: string; id: string }[]];
}>();

const selectedFields = ref<{ type: string; value: string; id: string }[]>([
  { type: UTM_TYPES[0], value: "", id: v4() }
]);

const typeOptions = computed(() => {
  const existingParams = selectedFields.value.map(({ type }) => type);
  return UTM_TYPES.filter((typeOption) => !existingParams.includes(typeOption));
});

const selectUtmType = (value: number, index: number) => {
  selectedFields.value[index].type = typeOptions.value[value] || "";
  emit("value:change", selectedFields.value);
};

const updateValue = (value: string, id: string) => {
  const updatedType = selectedFields.value.find(
    ({ id: existingId }) => existingId === id
  );
  if (updatedType) {
    updatedType.value = value;
  }
  emit("value:change", selectedFields.value);
};

const addType = () => {
  selectedFields.value.push({
    type: typeOptions.value[0],
    value: "",
    id: v4()
  });
  emit("value:change", selectedFields.value);
};

const deleteType = (id: string) => {
  const index = selectedFields.value.findIndex((input) => input.id === id);

  if (index === -1) {
    return;
  }

  selectedFields.value.splice(index, 1);
  emit("value:change", selectedFields.value);
};
</script>
